/**
 * src/utils/flagStripeGeometry.ts
 *
 * Cutting a building's footprint into side-by-side stripes.
 *
 * A `fill-extrusion` slab is one solid colour, so the only way to get a
 * vertical stripe onto a tower is to give the renderer a narrower tower:
 * the footprint is sliced into parallel strips and each strip extruded in
 * its own colour. That is what turns the UAE's three stripes into three
 * columns down the shaft instead of three bands around it.
 *
 * Two things have to be right or the stripes lie.
 *
 * The cuts are made in metres, not degrees. A degree of longitude is
 * shorter than a degree of latitude everywhere except the equator — in
 * Dubai by about a fifth — so slicing on raw coordinates would give
 * stripes of visibly unequal width, wider or narrower depending on which
 * way the building happens to face.
 *
 * The *direction* of the cuts is decided once for the whole building and
 * the *extent* is measured per part. A tower like the Burj Khalifa is a
 * stack of nested parts, each narrower than the one below: measuring the
 * extent across the whole complex would put its entire shaft inside one
 * stripe, so the tower would wear a single colour and the flag would live
 * on the podium. Measuring per part lets every part wear the whole flag,
 * with the stripe boundaries converging as the tower tapers — which is how
 * a flag painted on a tapering tower looks anyway. Sharing the direction
 * is what stops each part choosing its own and the flag twisting on the
 * way up.
 *
 * Clipping is Sutherland–Hodgman against two parallel half-planes. A band
 * is the intersection of two half-planes and so always convex, which is
 * the case that algorithm is exact for. A concave footprint that a band
 * crosses twice comes back as one ring joined along the cut line rather
 * than two; the covered area is right, and the join has no width to see.
 */

/** `[longitude, latitude]`. */
export type LonLat = [number, number];

/**
 * A polygon as GeoJSON coordinates: outer ring first, then holes.
 *
 * Loosely typed on the way in because that is how feature geometry arrives
 * from the wire — `number[][][]`, not tuples. What comes back out is
 * tightened to points.
 */
export type PolygonRings = ReadonlyArray<ReadonlyArray<ReadonlyArray<number>>>;

const M_PER_DEG_LAT = 110_540;
const M_PER_DEG_LON = 111_320;

/** Bearings are tried at this spacing when looking for the widest axis. */
const BEARING_STEP_DEGREES = 2;

/**
 * Slices thinner than this are dropped.
 *
 * Below roughly a metre a strip is a seam rather than a stripe, and a
 * sliver polygon is where extrusion artefacts come from.
 */
const MIN_SLICE_AREA_M2 = 1;

/**
 * The line a footprint is divided along, and how far it reaches.
 *
 * The direction is resolved once for the whole building, which is what
 * keeps every part's stripes running the same way. The extent is then
 * refitted to each part; see `refitAxis`.
 */
export interface StripeAxis {
  /** Compass bearing of the axis, degrees clockwise from north. */
  bearingDegrees: number;
  /** Unit vector of that bearing in the local metric frame. */
  u: [number, number];
  /** Projection of the whole footprint onto the axis, in metres. */
  min: number;
  max: number;
  /** Origin of the local metric frame. */
  lon0: number;
  lat0: number;
  /** Metres per degree at that origin. */
  mx: number;
  my: number;
}

type InputRing = ReadonlyArray<ReadonlyArray<number>>;

function ringsOf(polys: readonly PolygonRings[]): InputRing[] {
  const out: InputRing[] = [];
  for (const poly of polys) {
    if (!Array.isArray(poly)) continue;
    for (const ring of poly) {
      if (Array.isArray(ring) && ring.length >= 3) out.push(ring);
    }
  }
  return out;
}

function isFinitePoint(p: unknown): p is LonLat {
  return Array.isArray(p)
    && Number.isFinite(Number(p[0]))
    && Number.isFinite(Number(p[1]));
}

/** Unit vector for a compass bearing: north is `+y`, east is `+x`. */
function unitFor(bearingDegrees: number): [number, number] {
  const r = (bearingDegrees * Math.PI) / 180;
  return [Math.sin(r), Math.cos(r)];
}

/**
 * The axis to divide a footprint along, and the extent to divide.
 *
 * Without an explicit bearing the building's long face is found, so the
 * stripes run across it and all of them are visible from the side most
 * people are looking at.
 *
 * That is done by looking for the *narrowest* measurement and turning a
 * right angle, not by looking for the widest one. A rectangle is widest
 * across its diagonal — 82 m for one 80 m by 20 m — so measuring for width
 * would set every plain building's stripes at a slight angle to its own
 * walls. Narrowness has no such corner case: the least distance across a
 * shape is across its short face.
 *
 * Bearings are sampled rather than derived from a covariance matrix
 * because vertices are not spread evenly around a footprint — one long
 * wall may have two of them and a curved end twenty — and a matrix would
 * follow the vertices instead of the shape.
 */
export function stripeAxisFor(
  polys: readonly PolygonRings[],
  bearingDegrees?: number | null,
): StripeAxis | null {
  const points: LonLat[] = [];
  for (const ring of ringsOf(polys)) {
    // A closed ring repeats its first vertex, which would drag the frame
    // origin towards that corner for no reason.
    for (const p of opened(ring)) {
      if (isFinitePoint(p)) points.push([Number(p[0]), Number(p[1])]);
    }
  }
  if (points.length < 3) return null;

  let lon0 = 0;
  let lat0 = 0;
  for (const [lon, lat] of points) { lon0 += lon; lat0 += lat; }
  lon0 /= points.length;
  lat0 /= points.length;

  const my = M_PER_DEG_LAT;
  const mx = M_PER_DEG_LON * Math.cos((lat0 * Math.PI) / 180);
  if (!(mx > 0)) return null;

  const local = points.map(([lon, lat]) => [(lon - lon0) * mx, (lat - lat0) * my] as [number, number]);

  const spanAt = (bearing: number): { u: [number, number]; min: number; max: number } => {
    const u = unitFor(bearing);
    let min = Infinity;
    let max = -Infinity;
    for (const [x, y] of local) {
      const s = x * u[0] + y * u[1];
      if (s < min) min = s;
      if (s > max) max = s;
    }
    return { u, min, max };
  };

  let bearing: number;
  if (bearingDegrees != null && Number.isFinite(bearingDegrees)) {
    bearing = ((bearingDegrees % 180) + 180) % 180;
  } else {
    // Only half the circle: an axis and its reverse divide identically.
    let narrowest: { bearing: number; width: number } | null = null;
    for (let b = 0; b < 180; b += BEARING_STEP_DEGREES) {
      const span = spanAt(b);
      const width = span.max - span.min;
      if (!narrowest || width < narrowest.width) narrowest = { bearing: b, width };
    }
    if (!narrowest) return null;
    bearing = (narrowest.bearing + 90) % 180;
  }

  const span = spanAt(bearing);
  if (!(span.max - span.min > 0)) return null;

  return {
    bearingDegrees: bearing,
    u: span.u,
    min: span.min,
    max: span.max,
    lon0,
    lat0,
    mx,
    my,
  };
}

/**
 * The same axis, measured across one part instead of the whole building.
 *
 * Keeps the frame and the direction, so every part of a tower is cut the
 * same way, while letting each part carry the whole flag across its own
 * width. Null for a part with nothing to measure.
 */
export function refitAxis(axis: StripeAxis, polygon: PolygonRings): StripeAxis | null {
  let min = Infinity;
  let max = -Infinity;
  for (const ring of polygon) {
    if (!Array.isArray(ring)) continue;
    for (const [lon, lat] of opened(ring)) {
      const x = (lon - axis.lon0) * axis.mx;
      const y = (lat - axis.lat0) * axis.my;
      const s = x * axis.u[0] + y * axis.u[1];
      if (s < min) min = s;
      if (s > max) max = s;
    }
  }
  return max > min ? { ...axis, min, max } : null;
}

/** Signed area of a local-frame ring, in square metres. */
function areaOf(ring: Array<[number, number]>): number {
  let sum = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    sum += (ring[j][0] * ring[i][1]) - (ring[i][0] * ring[j][1]);
  }
  return Math.abs(sum) / 2;
}

/**
 * Keep the part of a ring on one side of a line perpendicular to `u`.
 *
 * `keep` is `'below'` for `dot(p, u) <= t` and `'above'` for the other
 * side. Vertices exactly on the line stay, so two adjacent bands share
 * their boundary rather than leaving a gap.
 */
function clipHalfPlane(
  ring: Array<[number, number]>,
  u: [number, number],
  t: number,
  keep: 'below' | 'above',
): Array<[number, number]> {
  const inside = (p: [number, number]): boolean => {
    const s = p[0] * u[0] + p[1] * u[1];
    return keep === 'below' ? s <= t : s >= t;
  };
  const cross = (a: [number, number], b: [number, number]): [number, number] => {
    const sa = a[0] * u[0] + a[1] * u[1];
    const sb = b[0] * u[0] + b[1] * u[1];
    const d = sb - sa;
    // Both ends on the line: no crossing to interpolate, take the end.
    const f = d === 0 ? 0 : (t - sa) / d;
    return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
  };

  const out: Array<[number, number]> = [];
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const a = ring[j];
    const b = ring[i];
    const aIn = inside(a);
    const bIn = inside(b);
    if (aIn !== bIn) out.push(cross(a, b));
    if (bIn) out.push(b);
  }
  return out;
}

/** Drop a ring's repeated closing vertex, if it has one. */
function opened(ring: InputRing): LonLat[] {
  const pts = ring.filter(isFinitePoint).map((p) => [Number(p[0]), Number(p[1])] as LonLat);
  if (pts.length > 1) {
    const first = pts[0];
    const last = pts[pts.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) pts.pop();
  }
  return pts;
}

export interface StripeSlice {
  /** Which stripe this is, counting from the low end of the axis. */
  index: number;
  /** GeoJSON polygon coordinates: outer ring first, then holes. */
  polygon: number[][][];
}

/**
 * One polygon cut into `count` stripes across the axis given.
 *
 * Pass an axis refitted to this polygon for a part that should wear the
 * whole flag, or the building-wide one for a part that should only wear
 * the stripes it stands under — a part outside that extent comes back with
 * fewer stripes than `count`, keeping the indices it does occupy.
 */
export function stripeSlices(
  polygon: PolygonRings,
  axis: StripeAxis,
  count: number,
): StripeSlice[] {
  const n = Math.floor(count);
  if (!(n > 0) || !Array.isArray(polygon) || polygon.length === 0) return [];
  if (n === 1) {
    return [{
      index: 0,
      polygon: polygon.map((ring) => ring.map((p: ReadonlyArray<number>) => [...p])),
    }];
  }

  const rings = polygon
    .map(opened)
    .filter((r) => r.length >= 3)
    .map((r) => r.map(([lon, lat]) => [
      (lon - axis.lon0) * axis.mx,
      (lat - axis.lat0) * axis.my,
    ] as [number, number]));
  if (rings.length === 0) return [];

  const span = axis.max - axis.min;
  const width = span / n;
  const out: StripeSlice[] = [];

  for (let i = 0; i < n; i += 1) {
    const low = axis.min + width * i;
    const high = i === n - 1 ? axis.max : axis.min + width * (i + 1);

    const clipped = rings
      .map((ring) => clipHalfPlane(
        clipHalfPlane(ring, axis.u, high, 'below'),
        axis.u,
        low,
        'above',
      ))
      .filter((ring) => ring.length >= 3 && areaOf(ring) >= MIN_SLICE_AREA_M2);
    if (clipped.length === 0) continue;

    out.push({
      index: i,
      polygon: clipped.map((ring) => {
        const back: number[][] = ring.map(([x, y]) => [
          axis.lon0 + x / axis.mx,
          axis.lat0 + y / axis.my,
        ]);
        back.push([back[0][0], back[0][1]]);
        return back;
      }),
    });
  }

  return out;
}

/**
 * Where the crown stops and the columns begin.
 *
 * A vertical arrangement still needs one horizontal cut: the hoist colour
 * caps the tower and the stripes hang below it. A part is split at that
 * height so a shaft running the full drop gets both, which is the same
 * reason a horizontal flag cuts its parts at band boundaries.
 *
 * Returns null for either piece the part does not reach.
 */
export function crownSplit(
  lowMeters: number,
  highMeters: number,
  peakMeters: number,
  crownShare: number,
): { crown: { low: number; high: number } | null; body: { low: number; high: number } | null } {
  const nothing = { crown: null, body: null };
  const low = Number.isFinite(lowMeters) ? Math.max(0, lowMeters) : 0;
  const high = Number.isFinite(highMeters) ? highMeters : NaN;
  if (!(high > low)) return nothing;

  const share = Number.isFinite(crownShare) ? Math.min(0.9, Math.max(0, crownShare)) : 0;
  if (!(peakMeters > 0) || !(share > 0)) {
    return { crown: null, body: { low, high } };
  }

  const cut = peakMeters * (1 - share);
  // A part wholly above the cut is all crown; wholly below, all columns.
  // The topmost part keeps anything above the recorded peak, so a spire
  // taller than the tallest mapped part is painted rather than truncated.
  if (low >= cut) return { crown: { low, high }, body: null };
  if (high <= cut) return { crown: null, body: { low, high } };
  return { crown: { low: cut, high }, body: { low, high: cut } };
}
